// The Briefing for the Email Workspace — the FETCHED kind.
//
// Unlike the search and image briefings, this one cannot be worked out on the client: the summary
// sentence is written by busy-ai-srv from the sender groups (POST ai/email-briefing, via the BFF).
// So this module holds only the two pure halves — what to ASK for, and how to read the ANSWER —
// while the store owns the request lifecycle.
//
// It keeps a client-side fallback on purpose. The counts are ours already, so a failed or slow AI
// leg degrades to a true, duller sentence instead of an empty strip.

import { briefing, countText, loadingBriefing, pendingRow } from './rows'
// Column-first flow (busy-my-web#530): fill the left column, then continue on the right.
import { LEFT_COLUMN_MAX, columnGridOrder, splitColumns } from './columnFlow'
import { ATTACHMENTS_ICON, ATTACHMENTS_FILTER, attachmentsRow, attachmentsTotal, isAttachmentCounts } from '@/lib/attachmentsBriefing'
import { briefingTotals, groupsLabel } from '@/lib/emailWorkspace'
import { groupSize } from '@/lib/emailListPaging'
import { TEXT, emailsLabel, groupName, groupsCountLabel, linkedEmailsLabel } from '@/lib/emailSession'

/** The request body busy-ai-srv expects for a briefing over these sender groups. */
export function emailBriefingRequest(groups, timeZoneId) {
  const totals = briefingTotals(groups || [])
  return {
    groups: groups || [],
    totalEmails: totals.emails,
    totalUnseen: totals.unread,
    timeZoneId: timeZoneId || 'UTC',
  }
}

/**
 * A cache key for one briefing request. The summary is only valid for the groups it was written
 * about, so archiving a sender — which changes the groups — has to invalidate it, while merely
 * re-opening the workspace must not re-ask.
 */
export function emailBriefingKey(groups) {
  const t = briefingTotals(groups || [])
  return `${(groups || []).length}:${t.emails}:${t.unread}`
}

/** What we can say from the counts alone, when the AI leg gave us nothing. */
export function emailFallbackSummary(groups) {
  const t = briefingTotals(groups || [])
  if (!t.groups) return 'Nothing is piling up from any one sender right now.'
  return `${t.emails} of your emails come from ${groupsLabel(t.groups)}, and ${t.unread} are unread.`
}

// ── The dashboard (busy-my-web#273, Figma ls-01 "Briefing Email Inbox") ─────────────────────────
//
// The frame writes the inbox as ENTRIES in two columns —
//
//   124 emails · 9 require action · 6 waiting for replies · 27 orders · 20 receipts
//   13 subscriptions · 4 email groups
//
// — each a real count over the same threads, and each a way in. Until #273 only two of them had a
// source (`v1/email/groups` answers about SENDERS), which is what the note further down this file
// recorded; `GET v1/email/briefing` now answers all of them in one call, so the note's condition is
// met and the rows are the frame's.
//
// The order below IS the card's reading order — the frame's, with "emails today" added after the
// total (the issue's own list, spec §6), the groups entry moved to 4th (busy-my-web#529), and since
// busy-my-web#603 the commerce pair moved up behind it. The two rows that are NOT entries — the
// possible-matches row and attachments — have their places in that same order too: `EMAIL_CARD_ORDER`
// below is this list with each of them spliced in after the entry it follows (`CARD_ROW_AFTER`).
// `emailBriefingColumns` splits that one order into two columns by POSITION: the first five rows
// are the left column and the rest continue on the right (busy-my-web#530). So there is one list,
// it is this one, and the layout is a presentation of it — which is what makes "put X n-th" a line
// moved in an array rather than an arithmetic change somewhere else.

/**
 * The entries, in the card's reading order: `key` is the row AND the filter the trail names it by.
 *
 * ORDER, COMMAS AND CAPITALS (busy-my-web#603, Saffet 2026-10-09, Slack p1791528140418409): "The
 * order of the email Briefing need to be changed … 1,501 Emails · 1 Email today · 26 Require action ·
 * 50 Email groups · 5 Possible Ongoing matches" on the left and "54 Orders · 72 Invoices, Receipts ·
 * 2,322 Attachments" on the right; "there needs to be a coma … 2,030"; "after each number it should
 * be a capital letter". So:
 *   • orders and receipts follow the groups entry (with the matches row between — see
 *     `CARD_ROW_AFTER`), and attachments follows receipts. The three entries he did not name —
 *     waiting, subscriptions, label groups — keep their old relative order and come after
 *     attachments (an open question on the issue, not a decision of his);
 *   • every count goes through `countText` ("2,322"), not only the total and today's;
 *   • the first word after the number is capitalised, and the receipts entry reads "Invoices,
 *     Receipts" — for every count, since "1 Invoice, Receipt" names neither thing.
 * That DEVIATES from frame 40006958-296686, whose labels are lower-case ("124 emails · 9 require
 * action"), on his request; the crumb and the Home modal heading read these same labels and say the
 * same words. Since busy-my-web#609 the capital is every Briefing's rule, not this card's alone
 * (`capitalAfterCount` in ./rows).
 *
 * The GLYPHS are the frame's own (Figma 40006958-296686, busy-my-web#316) — the icons were picked
 * from what the pack happened to hold when #273 wrote the rows, and the design names one per entry:
 * a mailbox for the total, an envelope with a dot for today's, an envelope with a star for the ones
 * that need answering, a clock for the waits, and the commerce three as direct / receipt-2 / crown.
 * They live here rather than in the strip because a row's icon is part of what the row IS.
 */
export const EMAIL_ENTRIES = [
  // `heading` is the word the OPEN entry's box title uses where it is not the row's own
  // (busy-my-web#615, Saffet 2026-10-09): "Briefing · All Emails" over the box. The crumb has no
  // words of its own since #623 — it repeats the row clicked ("1,051 Emails"). Every other entry's
  // title is its row's words with the count taken off (`entryWords`).
  { key: 'emails', icon: 'direct-inbox', field: 'emailsTotal', label: n => `${countText(n)} Email${n === 1 ? '' : 's'}`, heading: 'All Emails' },
  { key: 'today', icon: 'sms-notification', field: 'emailsToday', label: n => `${countText(n)} Email${n === 1 ? '' : 's'} today` },
  // "Need action", not "Require action" (busy-my-web#614, Saffet 2026-10-09, Slack
  // p1791535095924699: "Can we change 26 Require action to 26 Need action"). The crumb, the listing
  // title, the thread list's lines and Home's modal heading all read this label, so it is said once.
  { key: 'attention', icon: 'sms-star', field: 'requireAttention', label: n => `${countText(n)} Need action` },
  // FOURTH, and fourth by NAME rather than by rank (busy-my-web#529, Saffet 2026-09-17: "we have to
  // order the email list correctly — 50 email groups to be the 4th in the list"). This array has
  // always been the card's reading order, but the groups entry sat at the END of it — it was the one
  // entry that existed before #273 wrote the other seven, and it stayed where it had been appended.
  //
  // Saffet named ONE position that time, and only one moved: the groups entry is index 3. #603 then
  // moved the commerce pair up behind it (above); the groups entry is still 4th. Nothing here is
  // computed from a rank — the position IS this array's order, so a future "put X n-th" is a line
  // moved in this list and nothing else.
  //
  // Its own capitalised words rather than `groupsLabel`, which stays lower-case for the fallback
  // summary's sentence ("…come from 14 email groups").
  { key: 'groups', icon: 'sms-tracking', field: 'emailGroups', label: n => `${countText(n)} Email group${n === 1 ? '' : 's'}` },
  { key: 'orders', icon: 'direct', field: 'orders', label: n => `${countText(n)} Order${n === 1 ? '' : 's'}` },
  { key: 'receipts', icon: 'receipt-2', field: 'receipts', label: n => `${countText(n)} Invoices, Receipts` },
  // "reply"/"replies" is not a stem plus an "s" — the one entry whose singular has to be written
  // out, or a mailbox with one of these says "1 Waiting for replie".
  { key: 'waiting', icon: 'clock', field: 'waitingForReplies', label: n => `${countText(n)} Waiting for ${n === 1 ? 'reply' : 'replies'}` },
  { key: 'subscriptions', icon: 'crown', field: 'subscriptions', label: n => `${countText(n)} Subscription${n === 1 ? '' : 's'}` },
  // Leftover Gmail labels (2026-09-26): every label that doesn't power one of the entries above —
  // "we can have more groups if we can't put email to a group". Appended at the end rather than
  // placed by design decision (unlike the "groups" entry's Figma-dictated 4th position above),
  // since this entry has no frame of its own yet; reuses the "groups" icon as a placeholder.
  { key: 'labelGroups', icon: 'sms-tracking', field: 'labelGroups', label: n => `${countText(n)} Label group${n === 1 ? '' : 's'}` },
]

/**
 * WHERE the "N email groups" entry stands (busy-my-web#529): the 4th row, 0-based index 3.
 *
 * Stated as a constant so the rule Saffet gave can be asserted directly rather than by counting the
 * array above by eye — the order is data, and this is the one position in it that is a requirement.
 */
export const GROUPS_ENTRY_INDEX = 3

/**
 * How wide each placeholder bar is drawn, in px (busy-my-web#354) — about the width of the words
 * that will replace it at the card's 14px: "14,387 emails", "4 email groups", "12 attachments".
 *
 * Approximate on purpose. The exact width cannot be known before the count is (that is the whole
 * point of the placeholder), and a bar that is a few px off costs a few px of text reflow inside
 * one row, while a row that is not there at all costs the whole card a line.
 */
export const PENDING_WIDTH = { emails: 96, groups: 112, attachments: 104 }

/** The card's own glyph and title colour (frame 40006958-296686): a receipt outline, in near-black. */
export const EMAIL_TITLE_ICON = 'receipt-text'
export const EMAIL_TITLE_COLOR = '#111827'

/** The possible-matches row's glyph and its key (busy-my-web#316) — the frame's radar. */
export const MATCHES_ICON = 'radar-2'
export const MATCHES_KEY = 'relations'

/**
 * "2 Possible Ongoing matches" — the frame's own line for the offers, capitalised since #603.
 *
 * …and "6 Possible connections" when the count is the KNOWLEDGE GRAPH's (busy-my-web#710, frame
 * 40015353:452042): every suggested connection with an email on one side — to an Ongoing, a task,
 * a note … — not only the Ongoing matches, so the row names what it now opens.
 */
export function matchesLabel(count, graph = false) {
  const n = Number(count) || 0
  if (graph) return `${countText(n)} Possible connection${n === 1 ? '' : 's'}`
  return `${countText(n)} Possible Ongoing match${n === 1 ? '' : 'es'}`
}

/**
 * The two rows that are not entries, and the entry each one follows (busy-my-web#603).
 *
 * Saffet's order puts "5 Possible Ongoing matches" 5th — straight after "50 Email groups", closing
 * the left column — and "2,322 Attachments" straight after "72 Invoices, Receipts". Neither row is
 * an entry (no dashboard field, no category filter), so neither can sit in `EMAIL_ENTRIES`; naming
 * the entry each follows is what keeps their place a fact of the order rather than of the code that
 * assembles the card.
 */
export const CARD_ROW_AFTER = { [MATCHES_KEY]: 'groups', [ATTACHMENTS_FILTER]: 'receipts' }

/**
 * The whole card's reading order, as row keys: the entries with the matches and attachments rows
 * spliced in after the entries `CARD_ROW_AFTER` names. ONE flat list — `emailCardRows` sorts by it
 * and the column split reads positions off the result, so nothing else states an order.
 */
export const EMAIL_CARD_ORDER = EMAIL_ENTRIES.flatMap(entry => [
  entry.key,
  ...Object.keys(CARD_ROW_AFTER).filter(key => CARD_ROW_AFTER[key] === entry.key),
])

/**
 * The possible-Ongoing-matches row (busy-my-web#316, deviation 2).
 *
 * The same offers the "Possible relations" banner used to announce across the card's full width —
 * now one stat row like every other count, because it IS a count over the same mailbox. Null with
 * nothing to offer, the rule every row here follows: a briefing states what is there, and "0
 * possible Ongoing matches" reads as a fault rather than as calm.
 *
 * `relations` (not `filter`) is what marks it: pressing it opens the possible-matches WORKSPACE —
 * the match cards in the middle (busy-my-web#317) — and NOT the category narrowing a filter row
 * does, so the filter machinery (toggleFilter, emailCategory, the entry crumb) is left alone.
 *
 * Once that view IS open the row goes entirely and the breadcrumb carries it instead — the rule
 * busy-my-web#188 wrote for a depth the crumb is stating ("the row's only remaining job was its ✕,
 * and the trail's last segment now carries exactly that"). See `emailBriefing` below.
 */
export function matchesRow(count, { open = false, graph = false } = {}) {
  const n = Number(count) || 0
  if (n <= 0) return null
  return {
    key: MATCHES_KEY,
    icon: MATCHES_ICON,
    text: matchesLabel(n, graph),
    relations: true,
    active: !!open,
  }
}

/** The entry a key names — the one place a filter string is turned back into what it means. */
export function emailEntry(key) {
  return EMAIL_ENTRIES.find(e => e.key === key) || null
}

/** An entry's own counted words ("18 Emails today") — the card's row, the listing title, Home's modal heading. */
export function emailEntryLabel(key, counts) {
  const entry = emailEntry(key)
  if (!entry) return ''
  return entry.label(Number(counts?.[entry.field]) || 0)
}

// The count a label leads with ("1,051 " of "1,051 Emails") — what is taken off to leave its words.
const LEADING_COUNT_WORDS = /^\d[\d,]*\s+/

/** An entry's words without the count: "Need action", "Email today", "Invoices, Receipts". */
export function entryWords(key, count) {
  const entry = emailEntry(key)
  if (!entry) return ''
  return entry.label(Number(count) || 0).replace(LEADING_COUNT_WORDS, '')
}

/**
 * What the CRUMB calls an open entry: the row that was clicked, verbatim — "1,051 Emails",
 * "26 Need action", "54 Orders". busy-my-web#615 (Saffet 2026-10-09) first wrote it as "words · count"
 * ("All emails · 1,051") so the count lived in the crumb; busy-my-web#623 (Saffet 2026-10-09: "we
 * never have a .") dropped the middle dot — the crumb is the row you clicked, nothing else.
 * Its root was "Briefing" (not his "Email") until #619 (Saffet 2026-10-09) reversed it:
 * the email Briefing's root is now its own title, "Briefing Email Inbox", as ONE crumb
 * (`withEmailRootTitle`) — every other journey's root still reads "Briefing".
 *
 * Beside `emailEntryLabel` rather than instead of it: that one is still the row's own text, which
 * the listing, the commerce tabs and Home's modal heading are titled by.
 */
export function entryCrumbLabel(key, counts) {
  const entry = emailEntry(key)
  if (!entry) return ''
  const n = Number(counts?.[entry.field]) || 0
  return entry.label(n)
}

/**
 * The box's TITLE at an open entry (busy-my-web#615): "Briefing · All Emails", "Briefing · Need
 * action" — the same "Briefing · <name>" form an open sender group wears (`emailBriefing`).
 */
export function entryBriefingTitle(key, counts) {
  const entry = emailEntry(key)
  if (!entry) return ''
  const n = Number(counts?.[entry.field]) || 0
  return `Briefing · ${entry.heading || entryWords(key, n)}`
}

/**
 * The attachments entry's crumb and title (#615) — not an `EMAIL_ENTRIES` entry, same shape. The
 * crumb is "2,322 Attachments" (#623: count first, no dot — the row's own form).
 */
export const ATTACHMENTS_WORDS = 'Attachments'
export function attachmentsCrumbLabel(total) {
  return `${countText(total)} Attachment${total === 1 ? '' : 's'}`
}

/**
 * The user's own calendar day, "2026-10-21", on the browser's clock (busy-my-web#668) — the same
 * zone the dashboard is asked in (`timeZoneId`, Intl), so it names the day "N Emails today" counted.
 */
export function localDayKey(at = new Date()) {
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Were the counts in hand read on an EARLIER day than this one (busy-my-web#668)? Home asks for the
 * dashboard only when it has none, so a home page left open past midnight kept saying yesterday's
 * "5 Emails today" over a list that is fetched fresh — today's — when the row is pressed. Counts
 * with no day recorded are stale too: nothing says which day they were about.
 */
export function countsAreStale(day, now = new Date()) {
  return !day || day !== localDayKey(now)
}

/**
 * The counts an answer carries — or NULL when it carries none.
 *
 * `GET v1/email/briefing` is one leg of the strip, and a leg that answers something unreadable (a
 * gateway page, an empty envelope, a shape from a service that has moved on) has not landed. Saying
 * so here is what keeps the promise the briefing below makes: with no counts the strip degrades to
 * the two rows the client can work out on its own, rather than to an empty strip. Treating any
 * 200 as a dashboard is how a Briefing ends up stating NOTHING about a mailbox with 124 emails in
 * it — the one failure this module is written to avoid.
 *
 * An answer is a dashboard when it names at least one of the entries with a number. A mailbox that
 * is genuinely empty still does that (`emailsTotal: 0`), and is a different thing from silence.
 */
export function emailCounts(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  const named = EMAIL_ENTRIES.some((entry) => {
    const value = body[entry.field]
    return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
  })
  return named ? body : null
}

/**
 * The counts after one wait has ended (busy-my-web#293's "No longer waiting").
 *
 * A new copy rather than a mutation, because the counts object is what the strip renders and Vuex
 * state is replaced, never edited in place. Floored at zero: the count and the list are read from
 * two different clocks, and a dashboard that says "-1 waiting for replies" is worse than one that
 * is a minute out of date. Answers null for counts we never had, which is the honest "still
 * nothing to say" rather than an invented zero.
 */
export function countsAfterWaitResolved(counts) {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return null
  const left = Math.max(0, (Number(counts.waitingForReplies) || 0) - 1)
  return { ...counts, waitingForReplies: left }
}

/**
 * The dashboard's rows, from the one briefing call.
 *
 * An entry with NOTHING in it is dropped rather than drawn as a zero: the Briefing states what is
 * there, "0 orders" reads as a fault, and a row nobody can open is not a way in. That is the same
 * rule the two original rows were written under — it is now applied to eight.
 */
/**
 * ONE entry, as the card draws it (busy-my-web#209).
 *
 * The card is built from two different sources — the dashboard call, and the counts the client can
 * work out on its own from the sender groups — and the second used to write its rows by hand. That
 * is how "1 emails" reached the strip (a hand-written plural), and how the total ended up as the one
 * row on the card that opened NOTHING: it had no `filter`, so it rendered as a plain <p> while the
 * frame draws every count as a way in. Both sources now go through the entry, so a row says the same
 * words and opens the same listing however the number was arrived at.
 */
export function entryRow(key, count, { filter = true } = {}) {
  const entry = emailEntry(key)
  if (!entry) return null
  const n = Number(count) || 0
  return {
    key: entry.key,
    icon: entry.icon,
    text: entry.label(n),
    ...(filter ? { filter: entry.key } : {}),
  }
}

export function emailDashboardRows(counts) {
  return EMAIL_ENTRIES
    .map(entry => ({ entry, n: Number(counts?.[entry.field]) || 0 }))
    .filter(({ n }) => n > 0)
    .map(({ entry, n }) => ({
      key: entry.key,
      icon: entry.icon,
      text: entry.label(n),
      // An entry that is there is a way in — every one of them, which is the change #273 makes.
      filter: entry.key,
    }))
}

/**
 * The dashboard's rows with the groups entry's SLOT held while the sender groups are on the wire
 * (busy-my-web#607, Saffet 2026-10-09: "It is not visible for around 20 seconds then it appears").
 *
 * The groups entry is the one dashboard count a SECOND call answers: `emailGroups` is said by the
 * sender clusters of `v1/email/groups` (`withSystemGroupCount`), which lands long after
 * `v1/email/briefing`. #354 gave that wait a placeholder on the fallback path only, so on the
 * dashboard path the 4th slot (`GROUPS_ENTRY_INDEX`) was simply missing until the clusters landed,
 * and then everything below it moved down a line. Now, while `loading` and with no groups count to
 * state, the slot is a `pendingRow` — keyed 'groups', so `emailCardRows` sorts it into exactly the
 * place the real row will take. Where the dashboard already carries a non-zero `emailGroups` the
 * real row is drawn at once and nothing is added.
 */
export function withPendingGroups(rows, loading) {
  const list = rows || []
  if (!loading || list.some(r => r?.key === 'groups')) return list
  return [...list, pendingRow('groups', emailEntry('groups').icon, PENDING_WIDTH.groups)]
}

/**
 * The frame's two columns — the LEFT one filled first (busy-my-web#530).
 *
 * Saffet, 2026-09-17: "First we must list the 5 in the briefing on the left then 3 more to the
 * right so the left has a full list then continues to the right." So the split is POSITIONAL now:
 * rows 1–5 of the reading order are the left column, whatever follows is the right, and a card
 * with five rows or fewer has an empty right column.
 *
 * That replaces the membership split busy-my-web#338 wrote (`EMAIL_LEFT_COLUMN`, a key list naming
 * which entries belonged on the left). #338's own problem — a midpoint that moved with the mail,
 * because entries with nothing in them are dropped — is not back: five is a FIXED cap, not half of
 * however many rows survived, so the left column reads the same down to its last line whatever the
 * mailbox holds, and only the leftover moves. With the entries in #529's order that cap landed
 * where Saffet counted it then: emails, today, require action, email groups, waiting on the left;
 * orders, receipts, subscriptions on the right. In #603's order (`EMAIL_CARD_ORDER`) it lands where
 * he drew it on 2026-10-09: emails, today, require action, email groups, possible Ongoing matches
 * on the left; orders, invoices/receipts, attachments — then the rows he did not name — on the right.
 *
 * The flow itself is `lib/briefings/columnFlow.js`, shared with any other card that has to fill a
 * column before it starts the next one.
 */
export function emailBriefingColumns(rows) {
  return splitColumns(rows, LEFT_COLUMN_MAX)
}

/**
 * The same rows, in the order a two-column GRID has to receive them to read down each column the
 * way the frame does — each one stamped with the column it belongs to.
 *
 * You read the card downwards ("124 emails, 12 emails today, 9 require action…"), not across, and a
 * grid fills left-to-right, so the pairs are interleaved once, here, in the module that owns what
 * the entries ARE. The `col` a row carries is what keeps that true when the two columns are UNEVEN
 * — which, with the left one filled first, is every card with fewer than ten rows.
 */
export function emailBriefingGridOrder(rows) {
  return columnGridOrder(rows, LEFT_COLUMN_MAX)
}

/**
 * The frame's "Waiting for replies" row (frame 40006958:296258), now that there is something to
 * answer it with (busy-timeline-srv#26).
 *
 * It had no source before — `v1/email/groups` returns sender counts and nothing else — and the note
 * below says such rows are deliberately absent rather than shown as zeroes. That rule still holds:
 * the row appears only when a count is actually known, and a service that has not shipped the
 * waiting endpoint (`available: false`) falls back to the caller's own count of today's flagged
 * threads, or to no row at all.
 *
 * Pure, and its own function so what the row says can be checked without the whole strip.
 *
 * Its WORDS and its GLYPH are the entry's, not its own (busy-my-web#209). Two sources answer this
 * one row — the dashboard call and, where that has not landed, busy-timeline-srv's waiting endpoint
 * — and until now the second wrote it as a sentence with the verb first ("Waiting for 4 replies")
 * under a sender-tracking glyph, so the same fact read as two different rows depending on which leg
 * happened to answer. The frame (40006958-296686) writes it once, as a count and a noun under a
 * clock: "6 waiting for replies". So the label comes off `EMAIL_ENTRIES` — one wording, one place.
 *
 * @param waiting { count, overdue, available, fallbackCount }
 */
export function waitingRow(waiting) {
  const count = waitingCount(waiting)
  if (!count) return null

  const entry = emailEntry('waiting')
  // The overdue half is the part that is a problem, so it is said out loud where there is one — as
  // a second count after a middot, not as a clause: the frame's rows state numbers, never sentences.
  const overdue = Number(waiting.overdue || 0)
  const text = overdue ? `${entry.label(count)} · ${countText(overdue)} overdue` : entry.label(count)

  return { key: entry.key, icon: entry.icon, text, filter: entry.key }
}

/** How many replies the waiting endpoint's record says are awaited — its fallback where it has none. */
export function waitingCount(waiting) {
  if (!waiting) return 0
  return waiting.available === false
    ? Number(waiting.fallbackCount || 0)
    : Number(waiting.count || 0)
}

/**
 * The OPEN entry's level (busy-my-web#615): the active row with its crumb ("1,051 Emails" — the
 * row clicked, no dot, #623) as its `full` — the text lib/briefings/trail.js names the entry crumb by — and the box's
 * title for that level ("Briefing · All Emails"). Rows that are not the open entry pass through.
 *
 * @param rows the card's rows, the open one already marked `active`
 * @param counts the counts the rows were drawn from, in the dashboard's field names
 * @param attachmentTotal the attachments row's count — that row is not an `EMAIL_ENTRIES` entry
 */
export function openEntryLevel(rows, counts, attachmentTotal = 0) {
  let title = ''
  const marked = (rows || []).map((row) => {
    if (!row?.filter || !row.active) return row
    if (row.filter === ATTACHMENTS_FILTER) {
      title = `Briefing · ${ATTACHMENTS_WORDS}`
      return { ...row, full: attachmentsCrumbLabel(attachmentTotal) }
    }
    if (!emailEntry(row.filter)) return row
    title = entryBriefingTitle(row.filter, counts)
    return { ...row, full: entryCrumbLabel(row.filter, counts) }
  })
  return { rows: marked, title }
}

/**
 * The briefing for the inbox.
 *
 * @param groups the sender groups the workspace is showing
 * @param remote the store's fetch record for the AI sentence: { status, data }. Kept in the
 *   signature — it is the second positional argument every caller passes — but no longer read:
 *   since the 2026-09-02 clarification took the sentence off the card there is nothing on this
 *   card that fetch answers, and letting it gate the card is what made the wait visible (#339).
 * @param counts what the strip is drawn from — WHICHEVER counts the caller happens to hold, in any
 *   of the three shapes the app can hand it, told apart by shape so no caller has to say which:
 *     • the dashboard's own counts (`GET v1/email/briefing`, busy-my-web#273) → the eight entries;
 *     • the attachment counts (`documents/briefing`, busy-my-web#277) → the attachments row;
 *     • the waiting endpoint's `{ count, overdue, available, fallbackCount }` (busy-timeline-srv#26)
 *       → the "Waiting for replies" row.
 *   Null before anything lands.
 * @param attachments the attachment counts again, as their OWN argument, for the caller that holds
 *   the dashboard's counts as well — one strip can draw both. Same record, same rules: null and 0
 *   both mean "no row", which is the answer the strip has always given a category with no source.
 * @param matches the possible-Ongoing-matches record — `{ count, open }`, where `count` is how many
 *   thread offers `ongoing/suggestions` is holding and `open` says whether their list is unfolded
 *   under the card (busy-my-web#316). Null where nothing has been offered.
 * @param loading whether the GROUPS themselves are still on the wire (busy-my-web#339). Its own
 *   argument rather than another reading of `remote`, because those are two different fetches:
 *   `remote` is the AI sentence (which no row has drawn since the 2026-09-02 clarification), while
 *   this is the one call the card's own contents wait on. Only this one may put the skeleton up.
 * @param attachmentsLoading whether the ATTACHMENT counts are still on the wire (busy-my-web#354).
 *   A third clock again, and for the same reason the one above is its own: `documents/briefing` is
 *   asked by the box when its subject changes, on nobody else's schedule, so it can land seconds
 *   after the card that is already reading its other counts. While it is in flight the row is a
 *   PLACEHOLDER rather than absent — see `pendingRow`.
 */
export function emailBriefing(groups, remote, openCategory = null, openGroup = null, createStep = null, counts = null, attachments = null, matches = null, loading = false, attachmentsLoading = false) {
  const card = emailBriefingCard(groups, remote, openCategory, openGroup, createStep, counts, attachments, matches, loading, attachmentsLoading)
  return withEmailRootTitle(card, createStep)
}

/**
 * The email Briefing's ROOT CRUMB words (busy-my-web#619, Saffet 2026-10-09): "Home › Briefing
 * Email Inbox › ⟨All emails · 962⟩" — the inbox's own title, as one crumb, at every depth. Carried
 * on the card as `rootTitle` because the card's `title` moves with the level ("Briefing · All
 * Emails" at an entry, "Briefing · LinkedIn" in a group), while the root it climbs back to is
 * always the inbox. The create-an-ongoing step is not the inbox, so it gets none (and its trail
 * keeps the bare "Briefing", lib/briefings/trail.js).
 */
export function withEmailRootTitle(card, createStep = null) {
  if (!card || createStep) return card
  return { ...card, rootTitle: TEXT.briefingInbox }
}

function emailBriefingCard(groups, remote, openCategory, openGroup, createStep, counts, attachments, matches, loading, attachmentsLoading) {
  if (!groups) return null

  // The create-an-ongoing step owns the strip while it is open (busy-my-web#104, frames 6-8): the
  // strip stops describing the inbox and describes what is about to be linked — and keeps saying it
  // while the ongoing is being created, which is why this is checked before anything else.
  if (createStep) {
    const count = createStep.groups || 0
    return briefing({
      kind: 'emails',
      title: TEXT.briefingCreate,
      titleIcon: 'sms',
      rows: [
        { key: 'create-groups', icon: 'sms-tracking', text: groupsCountLabel(count) },
        { key: 'create-linked', icon: 'link', text: linkedEmailsLabel(createStep.emails || 0) },
        { key: 'create-future', icon: 'link-connection', text: TEXT.futureJoin },
      ],
    })
  }

  // The frames' own headings (busy-my-web#99, Fatemeh's first rule): the inbox is "Briefing Email
  // Inbox", and one open group is "Briefing · LinkedIn" — the same "Briefing · name" form every
  // other opened subject in the box wears.
  const title = openGroup ? `Briefing · ${groupName(openGroup)}` : TEXT.briefingInbox

  // Inside a group the strip stops describing the whole inbox: the frame shows one filter pill,
  // "438 emails", and the group's suggestions under it (rendered by EmailInboxPanel).
  if (openGroup) {
    return briefing({
      kind: 'emails',
      title,
      titleIcon: 'sms',
      // The group's THREADS — the rows opening it lists (lib/emailListPaging `groupSize`).
      rows: [{ key: 'group-emails', icon: 'direct-inbox', text: emailsLabel(groupSize(openGroup)) }],
    })
  }
  const totals = briefingTotals(groups)
  // No summary sentence (Figma 40009470-209128, design clarification 2026-09-02): the strip used to
  // close on the AI's line about the inbox ("Your inbox is quite full with 929 unread messages,
  // mostly from newsletters…"), or `emailFallbackSummary`'s duller true one. The entries above ARE
  // the briefing; prose repeating them under the grid is what the design leaves out. The request
  // side (`emailBriefingRequest`, the store's fetch) is untouched — `remote.data.summary` simply
  // has no row to land on.

  // Which of the two shapes was handed in (see the @param above): a dashboard body answers every
  // entry itself, so it owns the whole grid; anything else is the waiting endpoint's payload, and
  // that can only add its one row to the pair the client works out on its own.
  // The attachment record is looked for FIRST: it carries a `receipts` count of its own, which is
  // also the name of a dashboard entry, so asking "is this a dashboard?" of one would say yes.
  const attachmentRecord = isAttachmentCounts(attachments) ? attachments : (isAttachmentCounts(counts) ? counts : null)
  const dashboardCounts = isAttachmentCounts(counts) ? null : emailCounts(counts)
  const waitingLine = (dashboardCounts || attachmentRecord === counts) ? null : waitingRow(counts)

  // THE SKELETON, and only where it is the truth (busy-my-web#339).
  //
  // The card used to go blank for the whole of a fetch — and, worse, for the whole of the AI
  // sentence's fetch, which in production is a language-model round trip of several seconds. That
  // is what Saffet saw: ten seconds of nothing after pressing "Emails need attention", with the
  // dashboard counts sitting in the store the entire time because home had already asked for them.
  //
  // So the skeleton is now the LAST resort rather than the first state: it goes up only while the
  // groups are genuinely in flight AND the card has nothing true to say yet. The moment any count
  // is known the rows are drawn, and the rest fill in underneath them as they land. `remote` is
  // deliberately not consulted — the sentence it fetches has had no row on this card since the
  // 2026-09-02 clarification, so it can no longer hide the counts that do.
  const known = !!dashboardCounts || !!attachmentRecord || !!waitingLine || !!totals.emails || !!totals.groups
  if (loading && !known) {
    return loadingBriefing({ kind: 'emails', title, titleIcon: EMAIL_TITLE_ICON, titleColor: EMAIL_TITLE_COLOR })
  }

  // The DASHBOARD (busy-my-web#273). Every entry the frame writes, each one a count over the same
  // threads and each one a way in — the summary sentence stays where it always was, last, because
  // it describes what the entries above it are counting.
  //
  // Where the briefing call has not landed (a guest, an offline leg, the first paint), the two
  // counts the client holds on its own are still true, so the strip degrades to exactly the two
  // rows it had before rather than to nothing.
  //
  // …and while the groups call that feeds those two rows is still in flight, they are PLACEHOLDERS
  // rather than nothing (busy-my-web#354): the slot is the count's, from the moment the card knows
  // a count is coming, so the number replaces a shimmer instead of pushing the card open.
  const dashboard = dashboardCounts
    ? withPendingGroups(emailDashboardRows(dashboardCounts), loading)
    : [
      totals.emails
        // …and the total stays a plain FACT here, alone among the rows: the frame's every count is
        // a way in, but the listing behind this one is the very call that has not landed in this
        // branch (a guest, an offline leg, the first paint), so a button here opens an empty list
        // rather than the mailbox. The recorded #273 rule — "only the sender groups were ever a way
        // in" — is kept for exactly that reason; the dashboard path above opens all of them.
        ? entryRow('emails', totals.emails, { filter: false })
        : (loading ? pendingRow('emails', 'direct-inbox', PENDING_WIDTH.emails) : { key: 'emails', icon: 'direct-inbox', text: '' }),
      totals.groups
        ? entryRow('groups', totals.groups)
        : (loading ? pendingRow('groups', 'sms-tracking', PENDING_WIDTH.groups) : { key: 'groups', icon: 'sms-tracking', text: '', filter: 'groups' }),
      // Frame 40006958:296258's "Waiting for replies", where that endpoint is all that answered
      // (busy-timeline-srv#26). Present only when a count is actually KNOWN — a category row of
      // this frame stays absent rather than reading as a zero (busy-my-web#16).
      ...(waitingLine ? [waitingLine] : []),
    ]

  // The whole card, in READING order — `EMAIL_CARD_ORDER`, the offers row 5th under the groups and
  // attachments after the receipts (busy-my-web#603) — then interleaved into the order a two-column
  // grid needs (see `emailBriefingGridOrder`). Every row is placed by the SAME key list, which is
  // what keeps the offers row where Saffet put it on every mailbox, and not only on the one where all
  // eleven rows happen to have something in them.
  const ordered = emailCardRows(
    dashboard.map(r => (r.filter ? { ...r, active: openCategory === r.filter } : r)),
    matchesRow(matches?.count, { open: !!matches?.open, graph: !!matches?.graph }),
    // …and one of the frame's categories that DOES now have a source (busy-my-web#277):
    // attachments. It is a filter row like the groups above it — clicking opens the classified
    // list in the box. The frame has no such row; it stays because the count is real and the
    // mailbox's files are one of the things a person comes to the card for (#316, deviation 1).
    //
    // …and its own placeholder while `documents/briefing` is out (busy-my-web#354). The real row
    // wins the moment there is a count, and a count of ZERO still takes the slot away — a mailbox
    // with no files says nothing about files, which is the rule every row here is dropped by.
    attachmentsRow(attachmentRecord, { active: openCategory === 'attachments' })
      || (attachmentsLoading && !attachmentRecord
        ? pendingRow(ATTACHMENTS_FILTER, ATTACHMENTS_ICON, PENDING_WIDTH.attachments)
        : null),
  )

  // An OPEN entry reads as its own briefing (busy-my-web#615, Saffet 2026-10-09, Slack
  // p1791535225319699): "Briefing · All Emails" over the box and "All emails · 1,051" in the crumb,
  // so the count is said once, in the crumb. The fallback path's counts are put into the dashboard's
  // field names so one rule names the entry whichever leg answered.
  const levelCounts = dashboardCounts || { emailsTotal: totals.emails, emailGroups: totals.groups, waitingForReplies: waitingLine ? waitingCount(counts) : 0 }
  const level = openEntryLevel(ordered, levelCounts, attachmentsTotal(attachmentRecord))

  // …and while the possible-matches WORKSPACE is open (busy-my-web#317) the card states no counts at
  // all: the crumb above says where the box is ("Briefing ❯ 4 possible Ongoing matches ✕") and the
  // matches themselves are listed under it, which is exactly how #188 draws a depth the trail is
  // carrying. `matches` rides on the briefing value so the trail can read it without a second
  // argument threaded through the box — see lib/briefings/trail.js.
  // Open with a count of zero is a real state — the last card has just been answered — and it keeps
  // the crumb rather than dropping the user's way out from under them.
  const matchRecord = matches ? { count: Number(matches.count) || 0, open: !!matches.open, ...(matches.graph ? { graph: true } : {}) } : null
  if (matchRecord?.open) {
    return {
      ...briefing({ kind: 'emails', titleIcon: EMAIL_TITLE_ICON, titleColor: EMAIL_TITLE_COLOR, title, rows: [] }),
      matches: matchRecord,
    }
  }

  const card = briefing({
    kind: 'emails',
    // The card's own header (frame 40006958-296686): the receipt glyph and the title in near-black.
    // Indigo is what a LINK is in this box, and the line that merely names the card is not one —
    // the same correction the creation strip's header took in busy-my-web#280/#281.
    titleIcon: EMAIL_TITLE_ICON,
    titleColor: EMAIL_TITLE_COLOR,
    title: level.title || title,
    // Two columns, as the frame draws them — a hint for the strip, not a second list. Only where
    // there IS a dashboard: the fallback pair is the strip a guest (or a first paint) had before
    // #273, and "exactly the two rows it had before" includes the one column it drew them in.
    columns: dashboardCounts ? 2 : 1,
    rows: dashboardCounts ? emailBriefingGridOrder(level.rows) : level.rows,
  })
  // `levelTitle` is the title the strip draws UNDER the crumb (BriefingBlock.vue): the at-rest title
  // line is replaced by the crumb once a depth is open, and at an entry the title is still news —
  // it names the level the crumb's chip only counts (#615). Added only at an open entry, so every
  // other card is byte-for-byte the value it was.
  return level.title ? { ...card, levelTitle: level.title } : card
}

/**
 * The card's rows in READING ORDER — `EMAIL_CARD_ORDER`.
 *
 * It is one flat list and nothing is placed by index — which column a row lands in is decided
 * afterwards, by POSITION, by `emailBriefingColumns` (busy-my-web#530: five on the left, then the
 * right). `EMAIL_LEFT_COLUMN`, the key list #338 used to name the left column with, is gone with
 * that membership split: with the left column filled first there is no membership to state, and
 * keeping a second ordering rule beside the array's own was the thing that could put the offers row
 * somewhere the entries above it did not predict.
 *
 * Until busy-my-web#603 the offers row and attachments were simply APPENDED after the entries
 * (#316), which is where the frame draws them on a full mailbox. Saffet's order (2026-10-09) puts
 * them inside it — the offers 5th, attachments after the receipts — so the rows are now SORTED by
 * the card order rather than concatenated. The sort is stable and a key the order does not know
 * goes last in the order it came, so a caller's own row is never lost or reshuffled. Placeholder
 * rows (#354) carry the key of the row they will become, so they already stand in its place.
 *
 * Nulls are dropped: a row with no count is not a row (see `emailDashboardRows`).
 */
export function emailCardRows(rows, matchRow = null, attachmentRow = null) {
  const rank = (row) => {
    const at = EMAIL_CARD_ORDER.indexOf(row.key)
    return at === -1 ? EMAIL_CARD_ORDER.length : at
  }
  return [...(rows || []), matchRow, attachmentRow]
    .filter(Boolean)
    .map((row, i) => ({ row, i }))
    .sort((a, b) => (rank(a.row) - rank(b.row)) || (a.i - b.i))
    .map(({ row }) => row)
}
